import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Popover } from '@tempmee/react';
import { OutlineChevronDownIcon } from '@tempmee/react-icons';

/**
 * A top-right app-bar dropdown: a white label + chevron on the dark bar that
 * opens a `Popover` of `MenuItem`s.
 *
 * Only the trigger is themed for the dark bar — the popover is a white surface,
 * so its rows keep the default light tone. `surface="menu"` makes it borderless
 * and elevated to match the `Menu` surface; the default teal hairline reads as
 * an off-brand blue hanging off the bar.
 *
 * Mirrors the design system's Examples/Admin Shell story, which is the
 * reference for this shell.
 */
export const BarMenu = ({ label, icon, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef(null);

  return (
    <>
      {/* The DS Button rather than a native <button>: this repo bans native
          replaceable elements, and `color="inverse"` is what the design system
          provides for a control sitting on a dark bar. */}
      <Button
        ref={anchorRef}
        variant="text"
        color="inverse"
        onClick={() => setIsOpen((open) => !open)}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        startIcon={icon}
        endIcon={(
          <OutlineChevronDownIcon
            aria-hidden
            className={`size-5 shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
          />
        )}
      >
        <span className="whitespace-nowrap">{label}</span>
      </Button>

      <Popover
        open={isOpen}
        anchorEl={anchorRef.current}
        onClose={() => setIsOpen(false)}
        placement="bottom-end"
        surface="menu"
      >
        {/* The popover is the surface, so any click inside dismisses it — the
            chosen row has already acted. The rows themselves stay the
            interactive, keyboard-operable targets. */}
        {/* tabIndex={-1} makes the menu programmatically focusable without
            adding a tab stop, which is what the `menu` role requires. */}
        {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
        <div
          role="menu"
          tabIndex={-1}
          aria-label={label}
          onClick={() => setIsOpen(false)}
          className="flex flex-col gap-0.5"
        >
          {children}
        </div>
      </Popover>
    </>
  );
};

BarMenu.propTypes = {
  label: PropTypes.string.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node.isRequired,
};

BarMenu.defaultProps = { icon: null };
